import dayjs from "dayjs";
import { jsPDF } from "jspdf";

import { purchaseOrderService } from "~/services/purchasing";
import type { PurchaseOrder } from "~/types/purchasing";
import { purchaseOrderPdfStatusLabel, UNIT_LABELS } from "~/types/purchasing";

const LOGO_URL =
  "https://mdrvhekhimhcwydrpueo.supabase.co/storage/v1/object/public/uploads/assets/aliace-logo.jpg";

const formatNumber = (value: number): string => {
  return new Intl.NumberFormat("es-CL").format(value);
};

// Load image and convert to base64
const loadImage = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg"));
    };
    img.onerror = reject;
    img.src = url;
  });
};

export async function generatePurchaseOrderPdf(
  orderParam: PurchaseOrder
): Promise<void> {
  // If items are not loaded or lack required fields (e.g. description), fetch the full order
  let order = orderParam;
  const needsFullItems =
    !order.items ||
    order.items.length === 0 ||
    (order.items.length > 0 && order.items[0].description === undefined);

  if (needsFullItems) {
    try {
      order = await purchaseOrderService.getById(order.id);
    } catch (e) {
      console.error("Could not fetch order items:", e);
    }
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;

  // Try to load logo
  let logoBase64: string | null = null;
  try {
    logoBase64 = await loadImage(LOGO_URL);
  } catch (e) {
    console.warn("Could not load logo:", e);
  }

  // ==================== HEADER SECTION ====================
  let y = 12;

  // Logo on the left
  if (logoBase64) {
    doc.addImage(logoBase64, "JPEG", margin, y, 40, 20);
  }

  // Company info in center
  const centerX = pageWidth / 2;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("ALIMENTOS Y ACEITES SPA", centerX, y + 4, { align: "center" });

  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.text(
    "COMERCIALIZACION Y DISTRIBUCION DE PRODUCTOS ALIMENTICIOS",
    centerX,
    y + 9,
    { align: "center" }
  );
  doc.text("CARRETERA GENERAL SAN MARTIN N° 9340, BOD. 6", centerX, y + 13, {
    align: "center",
  });
  doc.text("QUILICURA, SANTIAGO", centerX, y + 17, { align: "center" });
  doc.text("TELEFONOS: (+56 2) 2681 6656", centerX, y + 21, {
    align: "center",
  });

  // Order number box on the right
  const boxX = pageWidth - margin - 45;
  const boxY = y;
  const boxWidth = 45;
  const boxHeight = 22;

  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.rect(boxX, boxY, boxWidth, boxHeight);

  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("ORDEN DE COMPRA", boxX + boxWidth / 2, boxY + 7, {
    align: "center",
  });

  doc.setFontSize(12);
  doc.text(
    `N° ${String(order.po_number).padStart(4, "0")}`,
    boxX + boxWidth / 2,
    boxY + 15,
    {
      align: "center",
    }
  );

  y += 30;

  // ==================== SUPPLIER & ORDER INFO ====================
  const infoBoxY = y;
  const infoBoxHeight = 45;
  doc.setLineWidth(0.3);
  doc.rect(margin, infoBoxY, pageWidth - 2 * margin, infoBoxHeight);

  // Vertical divider
  const dividerX = pageWidth / 2 + 15;
  doc.line(dividerX, infoBoxY, dividerX, infoBoxY + infoBoxHeight);

  // Left side - Supplier info
  y += 6;
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.text("SEÑOR(ES):", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(order.supplier?.name || "-", margin + 25, y);

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("R.U.T.:", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(order.supplier?.rut || "-", margin + 25, y);

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("GIRO:", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(order.supplier?.giro || "-", margin + 25, y);

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("DIRECCION:", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(order.supplier?.address || "-", margin + 25, y);

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("COMUNA/CIUDAD:", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(order.supplier?.comuna || "-", margin + 40, y);

  y += 5;
  doc.setFont("helvetica", "bold");
  doc.text("E.MAIL:", margin + 3, y);
  doc.setFont("helvetica", "normal");
  doc.text(order.supplier?.email || "-", margin + 25, y);

  // Right side - Order details
  let rightY = infoBoxY + 6;
  const rightX = dividerX + 3;

  doc.setFont("helvetica", "bold");
  doc.text("SANTIAGO:", rightX, rightY);
  doc.setFont("helvetica", "normal");
  doc.text(dayjs(order.issue_date).format("DD/MM/YYYY"), rightX + 35, rightY);

  rightY += 5;
  doc.setFont("helvetica", "bold");
  doc.text("FECHA DE ENTREGA:", rightX, rightY);
  doc.setFont("helvetica", "normal");
  doc.text(
    order.delivery_date ? dayjs(order.delivery_date).format("DD/MM/YYYY") : "-",
    rightX + 35,
    rightY
  );

  rightY += 5;
  doc.setFont("helvetica", "bold");
  doc.text("COD. DE PAGO:", rightX, rightY);
  doc.setFont("helvetica", "normal");
  doc.text(order.payment_terms || "-", rightX + 35, rightY);

  rightY += 5;
  doc.setFont("helvetica", "bold");
  doc.text("TELEFONO:", rightX, rightY);
  doc.setFont("helvetica", "normal");
  doc.text(order.supplier?.phone || "-", rightX + 35, rightY);

  rightY += 5;
  doc.setFont("helvetica", "bold");
  doc.text("N° REQUISICION:", rightX, rightY);
  doc.setFont("helvetica", "normal");
  doc.text("-", rightX + 35, rightY);

  rightY += 8;
  doc.setFont("helvetica", "bold");
  doc.text("ESTADO:", rightX, rightY);
  doc.setFont("helvetica", "normal");
  doc.text(purchaseOrderPdfStatusLabel(order.status), rightX + 35, rightY);

  // ==================== OBSERVATIONS ROW ====================
  y = infoBoxY + infoBoxHeight + 2;
  doc.setLineWidth(0.3);
  doc.rect(margin, y, pageWidth - 2 * margin, 8);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.text("OBSERVACIONES:", margin + 3, y + 5);
  doc.setFont("helvetica", "normal");
  const obsText = order.observations || "-";
  const truncatedObs =
    obsText.length > 80 ? obsText.substring(0, 77) + "..." : obsText;
  doc.text(truncatedObs, margin + 35, y + 5);

  // ==================== ITEMS TABLE ====================
  y += 12;

  // Table header - adjusted column widths (removed 716 column)
  // CODIGO, CANTIDAD, DETALLE, UNI, PRECIO UNIT, DCTO, TOTAL
  const colWidths = [18, 20, 72, 14, 22, 16, 28];
  const colHeaders = [
    "CODIGO",
    "CANTIDAD",
    "DETALLE",
    "UNI",
    "PRECIO UNIT",
    "DCTO",
    "TOTAL",
  ];
  const colX: number[] = [margin];

  // Calculate column positions
  for (let i = 1; i < colWidths.length; i++) {
    colX.push(colX[i - 1] + colWidths[i - 1]);
  }

  const rowHeight = 7; // base height for a single-line row
  const descLineHeight = 3; // vertical step between wrapped glosa lines
  const descWidth = colWidths[2] - 3; // usable text width inside the DETALLE column
  const items = order.items || [];
  // Render every item across as many pages as needed. Keep a minimum of 10 rows
  // so short OCs retain the template look (blank padding rows). Long OCs (e.g.
  // the Winkler supplier with 15+ items) flow onto additional pages instead of
  // overflowing a single sheet.
  const totalRows = Math.max(10, items.length);

  // Bottom totals/observations block height; grows when an ILA row is present
  // so the extra totals line never clips (non-ILA OCs keep the original height).
  const bottomHeight = order.ila_amount > 0 ? 43 : 38;
  // Space reserved below the last item row for the totals block + footer line,
  // so the table never collides with it and the totals always sit on the same
  // page as the final item.
  const bottomReserve = bottomHeight + 22;
  const rowGuard = pageHeight - bottomReserve;

  // Height of a row: single-line rows keep the base height; rows whose glosa
  // wraps grow to fit every wrapped line so the full description is shown.
  const rowHeightFor = (globalIndex: number): number => {
    if (globalIndex >= items.length) return rowHeight; // blank padding row
    const lines = doc.splitTextToSize(
      items[globalIndex].description || "",
      descWidth
    ) as string[];
    return Math.max(rowHeight, lines.length * descLineHeight + 4);
  };

  // ---- reusable table drawing helpers ----
  const drawColumnHeader = (headerY: number): number => {
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, headerY, pageWidth - 2 * margin, 6, "F");
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(margin, headerY, pageWidth - 2 * margin, 6);
    doc.setFontSize(6);
    doc.setFont("helvetica", "bold");
    colHeaders.forEach((header, i) => {
      doc.text(header, colX[i] + 1, headerY + 4);
    });
    colX.forEach((x) => {
      doc.line(x, headerY, x, headerY + 6);
    });
    doc.line(pageWidth - margin, headerY, pageWidth - margin, headerY + 6);
    return headerY + 6;
  };

  // Compact header for continuation pages; returns the y where the column
  // header should start.
  const drawContinuationHeader = (): number => {
    let hy = 14;
    doc.setTextColor(0);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("ALIMENTOS Y ACEITES SPA", margin, hy);
    doc.setFontSize(9);
    doc.text(
      `ORDEN DE COMPRA N° ${String(order.po_number).padStart(4, "0")}`,
      pageWidth - margin,
      hy,
      { align: "right" }
    );
    hy += 2;
    doc.setLineWidth(0.3);
    doc.line(margin, hy, pageWidth - margin, hy);
    hy += 4;
    doc.setFontSize(6);
    doc.setFont("helvetica", "normal");
    doc.text("(continuación)", margin, hy);
    return hy + 3;
  };

  // Outline + vertical column lines for one page's slice of the table body,
  // from segTop down to the bottom of the last row on that page (segBottom).
  const drawBodySegment = (segTop: number, segBottom: number): void => {
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.rect(margin, segTop, pageWidth - 2 * margin, segBottom - segTop);
    colX.forEach((x) => {
      doc.line(x, segTop, x, segBottom);
    });
    doc.line(pageWidth - margin, segTop, pageWidth - margin, segBottom);
  };

  const drawRow = (
    globalIndex: number,
    rowTop: number,
    firstOnPage: boolean
  ): void => {
    // Horizontal divider above every row except the first one on the page.
    if (!firstOnPage) {
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, rowTop, pageWidth - margin, rowTop);
      doc.setDrawColor(0);
    }

    // Padding rows (short OCs) leave the row blank.
    if (globalIndex >= items.length) return;

    const item = items[globalIndex];
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    const baseY = rowTop + 5;

    // Code - use a simple index-based code
    doc.text(String(globalIndex + 1).padStart(3, "0"), colX[0] + 2, baseY);

    // Quantity
    doc.text(formatNumber(item.quantity), colX[1] + 2, baseY);

    // Description (glosa) — wrapped across as many lines as needed so the full
    // text is shown instead of being truncated. The row grows to fit (see
    // rowHeightFor), so a long glosa always has enough space.
    const descLines = doc.splitTextToSize(
      item.description || "",
      descWidth
    ) as string[];
    descLines.forEach((line, lineIndex) => {
      doc.text(line, colX[2] + 2, baseY + lineIndex * descLineHeight);
    });

    // Unit
    const unitLabel =
      UNIT_LABELS[item.unit]?.substring(0, 3).toUpperCase() ||
      item.unit.substring(0, 3).toUpperCase();
    doc.text(unitLabel, colX[3] + 2, baseY);

    // Unit price (gross, per unit)
    doc.text(formatNumber(item.unit_price), colX[4] + 2, baseY);

    // Per-line discount (cotización Rec/Desc): percentage shows its rate, a fixed amount shows
    // the resolved value, none shows "-". The TOTAL below is the net (subtotal already net).
    const discountText =
      item.discount_type === "percentage" && item.discount_value > 0
        ? `${formatNumber(item.discount_value)}%`
        : item.discount_type === "amount" && (item.discount_amount ?? 0) > 0
          ? formatNumber(item.discount_amount)
          : "-";
    doc.text(discountText, colX[5] + 2, baseY);

    // Total (net = quantity * unit_price - line discount)
    doc.text(formatNumber(item.subtotal), colX[6] + 2, baseY);
  };

  // ---- render the table, paginating as needed ----
  let bodyTop = drawColumnHeader(y);
  let rowTop = bodyTop;
  let firstOnPage = true;

  for (let index = 0; index < totalRows; index++) {
    const rowH = rowHeightFor(index);
    // Break to a new page when the next row would cross into the reserved
    // footer zone (but always keep at least one row per page).
    if (!firstOnPage && rowTop + rowH > rowGuard) {
      drawBodySegment(bodyTop, rowTop);
      doc.addPage();
      bodyTop = drawColumnHeader(drawContinuationHeader());
      rowTop = bodyTop;
      firstOnPage = true;
    }
    drawRow(index, rowTop, firstOnPage);
    rowTop += rowH;
    firstOnPage = false;
  }
  // Close the final (or only) segment box.
  drawBodySegment(bodyTop, rowTop);

  // ==================== BOTTOM SECTIONS ====================
  y = rowTop + 5;

  // If an unusually tall final row left no room, move the totals block (and
  // footer) to a fresh page instead of overflowing the sheet.
  if (y + bottomHeight + 12 > pageHeight) {
    doc.addPage();
    y = margin + 5;
  }

  // Calculate widths - left gets more space for text
  const leftColWidth = (pageWidth - 2 * margin) * 0.42;
  const midColWidth = (pageWidth - 2 * margin) * 0.3;
  const rightColWidth = (pageWidth - 2 * margin) * 0.28;

  // Left column - Observaciones Generales
  doc.setLineWidth(0.3);
  doc.rect(margin, y, leftColWidth, bottomHeight);
  doc.setFontSize(6);
  doc.setFont("helvetica", "bold");
  doc.text("OBSERVACIONES GENERALES", margin + 2, y + 4);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(5);

  // Each bullet point as separate paragraph with proper wrapping
  const obsBullets = [
    "-SE REQUIERE QUE O.C. SUSCRITA SEA INCLUIDA EN EL CAMPO DE REFERENCIA DE LA FACTURA Y SE ADJUNTE A SU FACTURA Y/O GUIA DE DESPACHO, DE LO CONTRARIO NO SERAN RECEPCIONADOS.",
    "-EN LA FACTURA EL CERTIFICADO DE CALIDAD O FICHA TECNICA DE LOS PRODUCTOS INDICADOS EN LA PRESENTE ORDEN DE COMPRA, DE LO CONTRARIO LOS PRODUCTOS NO SERAN RECEPCIONADOS.",
    "-ADICIONALMENTE AL CORREO DE ENVIO XML Y PDF INDICADO EN DATOS DE FACTURACION, ENVIAR COPIA Y DATOS BANCARIOS A: proveedores@aliace.cl",
  ];

  let obsY = y + 8;
  obsBullets.forEach((bullet) => {
    const wrappedLines = doc.splitTextToSize(bullet, leftColWidth - 4);
    wrappedLines.forEach((line: string) => {
      doc.text(line, margin + 2, obsY);
      obsY += 2.5;
    });
    obsY += 1; // Extra space between bullet points
  });

  // Middle column - Datos de Facturacion
  const midColX = margin + leftColWidth;
  doc.rect(midColX, y, midColWidth, bottomHeight);
  doc.setFontSize(6);
  doc.setFont("helvetica", "bold");
  doc.text("DATOS DE FACTURACION", midColX + 2, y + 4);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(5);
  doc.text("EMPRESA ALIMENTOS Y ACEITES SPA", midColX + 2, y + 10);
  doc.text("R.U.T.: 76.555.808-2", midColX + 2, y + 15);
  doc.text("DIR.: CARRETERA GENERAL SAN MARTIN", midColX + 2, y + 20);
  doc.text("N° 9340, BOD. 6, QUILICURA, SANTIAGO.", midColX + 2, y + 25);
  doc.text("E.MAIL DTE: aliacerecepciondte@gmail.com", midColX + 2, y + 30);

  // Right column - Totales
  const rightColX = midColX + midColWidth;
  doc.rect(rightColX, y, rightColWidth, bottomHeight);
  doc.setFontSize(6);
  doc.setFont("helvetica", "bold");
  doc.text("TOTALES", rightColX + rightColWidth - 15, y + 4);

  doc.setFontSize(6);
  let totY = y + 10;

  // Order-level discount comes off the neto before IVA, so MONTO AFECTO (the IVA base) is the
  // discounted neto. A percentage discount shows its rate next to the label.
  const discountAmount = order.discount_amount ?? 0;
  const discountLabel =
    order.discount_type === "percentage" && order.discount_value > 0
      ? `DESCUENTO (${formatNumber(order.discount_value)}%):`
      : "DESCUENTO:";

  doc.setFont("helvetica", "normal");
  doc.text(discountLabel, rightColX + 2, totY);
  doc.text(
    discountAmount > 0 ? formatNumber(discountAmount) : "-",
    rightColX + rightColWidth - 3,
    totY,
    { align: "right" }
  );

  // Exento de IVA: a VAT-exempt OC carries the discounted neto under MONTO EXENTO (and 0 IVA);
  // an afecta OC carries it under MONTO AFECTO (the IVA base) as before.
  const netoAmount = order.subtotal - discountAmount;
  const isExento = order.is_exento ?? false;

  totY += 5;
  doc.text("MONTO AFECTO:", rightColX + 2, totY);
  doc.text(
    isExento ? "-" : formatNumber(netoAmount),
    rightColX + rightColWidth - 3,
    totY,
    {
      align: "right",
    }
  );

  totY += 5;
  doc.text("MONTO EXENTO:", rightColX + 2, totY);
  doc.text(
    isExento ? formatNumber(netoAmount) : "-",
    rightColX + rightColWidth - 3,
    totY,
    { align: "right" }
  );

  totY += 5;
  doc.text("19% I.V.A.:", rightColX + 2, totY);
  doc.text(
    formatNumber(order.tax_amount),
    rightColX + rightColWidth - 3,
    totY,
    {
      align: "right",
    }
  );

  // ILA (Impuesto Adicional a las Bebidas Alcohólicas y Analcohólicas) — only when present.
  if (order.ila_amount > 0) {
    totY += 5;
    doc.text("ILA:", rightColX + 2, totY);
    doc.text(formatNumber(order.ila_amount), rightColX + rightColWidth - 3, totY, {
      align: "right",
    });
  }

  totY += 5;
  doc.setFont("helvetica", "bold");
  doc.text("TOTAL:", rightColX + 2, totY);
  doc.text(formatNumber(order.total), rightColX + rightColWidth - 3, totY, {
    align: "right",
  });

  // ==================== FOOTER ====================
  y += bottomHeight + 5;
  doc.setFontSize(6);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0);
  doc.text("EMITIDA POR:", margin, y);
  doc.setFont("helvetica", "normal");
  doc.text("Aliace", margin + 25, y);

  // Page footer on every page: generated-date, plus "Página X de Y" when the
  // OC spans more than one sheet.
  const pageCount = doc.getNumberOfPages();
  const generatedAt = dayjs().format("DD/MM/YYYY HH:mm");
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(150, 150, 150);
    const footerText =
      pageCount > 1
        ? `Documento generado el ${generatedAt}  ·  Página ${p} de ${pageCount}`
        : `Documento generado el ${generatedAt}`;
    doc.text(footerText, pageWidth / 2, pageHeight - 5, { align: "center" });
  }
  doc.setTextColor(0);

  // Open PDF in new tab
  const pdfBlob = doc.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, "_blank");
}
